import React, { createContext, useContext, useEffect, useState } from 'react';
import { DEFAULT_LINK, getLinkForCountry, setDynamicLinks } from '../lib/countryLinks';
import { trackPageView } from '../lib/tracker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const CountryContext = createContext({
  country: null,
  source: null,
  link: DEFAULT_LINK,
});

const STORAGE_KEY = 'mbe_country_v1';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

const safeGetSession = (key) => {
  try {
    return window.sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const safeSetSession = (key, value) => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch (e) {
    /* noop */
  }
};

const applyToWindow = (code, source) => {
  try {
    window.__mbeCountry = code || null;
    window.__mbeCountrySource = source || null;
  } catch (e) {
    /* noop */
  }
};

const notifyPixel = (code, source) => {
  try {
    if (typeof window.fbq === 'function' && code) {
      window.fbq('trackCustom', 'PageViewCountry', {
        country: code,
        source,
      });
    }
  } catch (e) {
    /* noop */
  }
};

export const CountryProvider = ({ children }) => {
  const [country, setCountry] = useState(null);
  const [source, setSource] = useState(null);
  const [link, setLink] = useState(DEFAULT_LINK);

  useEffect(() => {
    let cancelled = false;

    // Load dynamic country->link map from backend (falls back to static if fails)
    fetch(`${BACKEND_URL}/api/country-links`)
      .then((r) => (r.ok ? r.json() : {}))
      .then((map) => {
        if (!cancelled && map && typeof map === 'object') {
          setDynamicLinks(map);
          // If country already detected, refresh link with the new map
          setLink((prev) => (country ? getLinkForCountry(country) : prev));
        }
      })
      .catch(() => {});

    const setDetected = (code, src) => {
      if (cancelled) return;
      const upper = code ? code.toUpperCase() : null;
      setCountry(upper);
      setSource(src);
      setLink(getLinkForCountry(upper));
      applyToWindow(upper, src);
      notifyPixel(upper, src);
      // Fire page_view to our own analytics backend
      try {
        trackPageView({ country: upper, source: src });
      } catch (e) {
        /* noop */
      }
      if (upper) {
        safeSetSession(
          STORAGE_KEY,
          JSON.stringify({ code: upper, ts: Date.now(), src })
        );
      }
    };

    // 1) URL parameter (?c=SG or ?country=SG) — highest priority for paid ads
    try {
      const params = new URLSearchParams(window.location.search);
      const paramCountry = params.get('c') || params.get('country');
      if (paramCountry && paramCountry.length >= 2) {
        setDetected(paramCountry, 'url');
        return;
      }
    } catch (e) {
      /* noop */
    }

    // 2) Cached session value
    const cached = safeGetSession(STORAGE_KEY);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (
          parsed &&
          parsed.code &&
          Date.now() - parsed.ts < CACHE_TTL_MS
        ) {
          setDetected(parsed.code, parsed.src || 'cache');
          return;
        }
      } catch (e) {
        /* noop */
      }
    }

    // 3) IP-based geo detection (api.country.is — free & unlimited)
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3500);
    fetch('https://api.country.is', { signal: controller.signal })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('geo failed'))))
      .then((data) => {
        clearTimeout(timeout);
        if (data && data.country) {
          setDetected(data.country, 'geo');
        } else {
          try { trackPageView({ country: null, source: 'fallback' }); } catch (e) {}
        }
      })
      .catch(() => {
        clearTimeout(timeout);
        applyToWindow(null, 'fallback');
        try { trackPageView({ country: null, source: 'fallback' }); } catch (e) {}
      });

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      try {
        controller.abort();
      } catch (e) {
        /* noop */
      }
    };
  }, []);

  return (
    <CountryContext.Provider value={{ country, source, link }}>
      {children}
    </CountryContext.Provider>
  );
};

export const useCountryLink = () => {
  const ctx = useContext(CountryContext);
  return ctx || { country: null, source: null, link: DEFAULT_LINK };
};

export default CountryContext;
